import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  HvDatePicker,
  HvDatePickerProps,
  HvListContainer,
  HvListItem,
} from "@hitachivantara/uikit-react-core";

import { useTimeSettings } from "lib/api";

import { buildPeriods, Periods } from "./utils/periods";

import useStyles from "./styles";

export interface DatepickerProps extends HvDatePickerProps {
  onDateRangeChange?: (startDate?: Date, endDate?: Date) => void;
  startDate?: Date;
  endDate?: Date;
}

const IncidentDatePicker: React.FC<DatepickerProps> = ({
  onDateRangeChange,
  startDate: initialStartDate,
  endDate: initialEndDate,
  ...others
}) => {
  const classes = useStyles();
  const { t } = useTranslation("incident");

  const [startDate, setStartDate] = useState<Date | undefined>(
    initialStartDate
  );
  const [endDate, setEndDate] = useState<Date | undefined>(initialEndDate);

  useEffect(() => {
    setStartDate(initialStartDate);
  }, [initialStartDate]);

  useEffect(() => {
    setEndDate(initialEndDate);
  }, [initialEndDate]);

  const { timezone } = useTimeSettings();

  const handleClick = useCallback(
    (period: keyof Periods) => {
      const periods: Periods = buildPeriods(timezone);

      setStartDate(periods[period].start.toDate());
      setEndDate(periods[period].end.toDate());
    },
    [timezone]
  );

  const onCancel = () => {
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
  };

  const options = (
    <HvListContainer
      role="menu"
      style={{ padding: "40px 20px", minWidth: 160 }}
      interactive
    >
      <HvListItem role="menuitem" onClick={() => handleClick("today")}>
        {t("datepicker.list.today")}
      </HvListItem>
      <HvListItem role="menuitem" onClick={() => handleClick("yesterday")}>
        {t("datepicker.list.yesterday")}
      </HvListItem>
      <HvListItem role="menuitem" onClick={() => handleClick("last7days")}>
        {t("datepicker.list.last7Days")}
      </HvListItem>
      <HvListItem role="menuitem" onClick={() => handleClick("last30days")}>
        {t("datepicker.list.last30Days")}
      </HvListItem>
      <HvListItem role="menuitem" onClick={() => handleClick("thisMonth")}>
        {t("datepicker.list.thisMonth")}
      </HvListItem>
      <HvListItem role="menuitem" onClick={() => handleClick("lastMonth")}>
        {t("datepicker.list.lastMonth")}
      </HvListItem>
      <HvListItem role="menuitem" onClick={() => handleClick("thisYear")}>
        {t("datepicker.list.thisYear")}
      </HvListItem>
      <HvListItem role="menuitem" onClick={() => handleClick("lastYear")}>
        {t("datepicker.list.lastYear")}
      </HvListItem>
    </HvListContainer>
  );

  return (
    <HvDatePicker
      aria-label={t("datepicker.ariaLabel")}
      placeholder={t("datepicker.placeholder")}
      label={t("datepicker.label")}
      rangeMode
      startAdornment={options}
      startValue={startDate}
      endValue={endDate}
      labels={{
        applyLabel: t("datepicker.labels.applyLabel"),
        cancelLabel: t("datepicker.labels.cancelLabel"),
        clearLabel: t("datepicker.labels.clearLabel"),
      }}
      onChange={onDateRangeChange}
      onCancel={onCancel}
      className={classes.incidentDatepicker}
      showClear
      {...others}
    />
  );
};
export default IncidentDatePicker;
